import { Navigate } from 'react-router-dom';

import { AffiliateLayout, PatternStudioLayout } from '@/app/layouts';

import {
    AffiliateCommissionsPage,
    AffiliateCustomPlansPage,
    AffiliateDashboardPage,
    AffiliateReferralDetailPage,
    AffiliateReferralsPage,
} from '@/pages/affiliate';
import {
    ForgotPasswordPage,
    GoogleAwaitPage,
    ResetPasswordPage,
    SignInPage,
    SignUpPage,
    SignUpViaEmailPage,
} from '@/pages/auth';
import { BoostCheckoutPage } from '@/pages/checkout';
import { DashboardPage } from '@/pages/dashboard';
import { ForbiddenPage, NotFoundPage, ThankYouPage } from '@/pages/general';
import { RedirectPage } from '@/pages/home';
import { LotSizeCalculatorPage } from '@/pages/lot-size-calculator';
import { NewsCalendarPage } from '@/pages/news-calendar';
import { OnboardingPage } from '@/pages/onboarding';
import {
    PatternStudioLabPage,
    PatternStudioLivePage,
    PatternStudioOverviewPage,
} from '@/pages/pattern-studio';
import { ProfileManagementPage } from '@/pages/profile-management';
import { RefreshPage } from '@/pages/refresh';
import {
    TradingJournalAgentPage,
    TradingJournalPage,
} from '@/pages/trading-journal';
import { UpdateDetailPage, UpdatesPage } from '@/pages/updates';

import { FeatureAccessGuard } from '@/features/guard';

import * as route from '@/shared/constants/router';

import type { RouterConfig } from '../types/router-config';

export const ROUTER_CONFIG: RouterConfig = {
    auxiliaryRoutes: {
        [route.AUXILIARY_ROUTES.forbidden]: {
            path: route.getForbiddenPath(),
            element: <ForbiddenPage />,
        },
        [route.AUXILIARY_ROUTES.notFound]: {
            path: route.getNotFoundPath(),
            element: <NotFoundPage />,
        },
        [route.AUXILIARY_ROUTES.refresh]: {
            path: route.getRefreshPath(),
            element: <RefreshPage />,
        },
    },
    authRoutes: {
        [route.AUTH_ROUTES.signIn]: {
            path: route.getSignInPath(),
            element: <SignInPage />,
        },
        [route.AUTH_ROUTES.signUp]: {
            path: route.getSignUpPath(),
            children: [
                {
                    index: true,
                    element: <SignUpPage />,
                },
                {
                    path: route.getSignUpViaEmailPath(),
                    element: <SignUpViaEmailPage />,
                },
            ],
        },
        [route.AUTH_ROUTES.passwordRecovery]: {
            path: route.getPasswordRecoveryPath(),
            children: [
                {
                    index: true,
                    element: <ForgotPasswordPage />,
                },
                {
                    path: route.getResetPasswordPath(),
                    element: <ResetPasswordPage />,
                },
            ],
        },
        [route.AUTH_ROUTES.googleAwait]: {
            path: route.getGoogleProcessingPath(),
            element: <GoogleAwaitPage />,
        },
    },
    onboardingRoutes: {
        [route.PRIVATE_ROUTES.onboarding]: {
            path: route.getOnboardingPath(),
            element: <OnboardingPage />,
        },
        [route.PRIVATE_ROUTES.thankYou]: {
            path: route.getThankYouPath(),
            element: <ThankYouPage />,
        },
    },
    privateRoutes: {
        [route.PRIVATE_ROUTES.redirect]: {
            element: <RedirectPage />,
            index: true,
        },
        [route.PRIVATE_ROUTES.dashboard]: {
            path: route.getDashboardPath(),
            element: <DashboardPage />,
        },
        [route.PRIVATE_ROUTES.updates]: {
            path: route.getUpdatesPath(),
            element: <UpdatesPage />,
        },
        [route.PRIVATE_ROUTES.updateDetail]: {
            path: route.getUpdateDetailPath(),
            element: <UpdateDetailPage />,
        },
        [route.PRIVATE_ROUTES.tradingJournal]: {
            path: route.getTradingJournalPath(),
            element: <TradingJournalPage />,
        },
        [route.PRIVATE_ROUTES.tradingJournalAgent]: {
            path: '/trading-journal/agent/:conversationId?',
            element: <TradingJournalAgentPage />,
        },
        [route.PRIVATE_ROUTES.profileManagement]: {
            path: route.getProfileManagementPath(),
            element: <ProfileManagementPage />,
            children: [
                {
                    path: route.getProfileManagementGeneralPath(),
                    element: <ProfileManagementPage />,
                },
                {
                    path: route.getProfileManagementPasswordPath(),
                    element: <ProfileManagementPage />,
                },
                {
                    path: route.getProfileManagementDevicesPath(),
                    element: <ProfileManagementPage />,
                },
                {
                    path: route.getProfileManagementBillingPath(),
                    element: <ProfileManagementPage />,
                },
                {
                    path: route.getProfileManagementDeleteAccountPath(),
                    element: <ProfileManagementPage />,
                },
            ],
        },
        [route.PRIVATE_ROUTES.affiliate]: {
            path: route.getAffiliatePath(),
            element: <AffiliateLayout />,
            children: [
                {
                    index: true,
                    element: (
                        <Navigate
                            replace
                            to={route.getAffiliateDashboardPath()}
                        />
                    ),
                },
                {
                    path: route.getAffiliateDashboardPath(),
                    element: <AffiliateDashboardPage />,
                },
                {
                    path: route.getAffiliateReferralsPath(),
                    element: <AffiliateReferralsPage />,
                },
                {
                    path: route.getAffiliateReferralDetailPath(),
                    element: <AffiliateReferralDetailPage />,
                },
                {
                    path: route.getAffiliateCommissionsPath(),
                    element: <AffiliateCommissionsPage />,
                },
                {
                    path: route.getAffiliateCustomPlansPath(),
                    element: <AffiliateCustomPlansPage />,
                },
            ],
        },
        [route.PRIVATE_ROUTES.patternStudio]: {
            element: (
                <FeatureAccessGuard requiredFeature="pattern-studio">
                    <PatternStudioLayout />
                </FeatureAccessGuard>
            ),
            children: [
                {
                    path: route.getPatternStudioOverviewPath(),
                    element: <PatternStudioOverviewPage />,
                },
                {
                    path: route.getPatternStudioLivePath(),
                    element: <PatternStudioLivePage />,
                },
                {
                    path: route.getPatternStudioLabPath(),
                    element: <PatternStudioLabPage />,
                },
            ],
        },
        [route.PRIVATE_ROUTES.classicTools]: {
            children: [
                {
                    path: route.getLotSizeCalculatorPath(),
                    element: <LotSizeCalculatorPage />,
                },
                {
                    path: route.getNewsCalendarPath(),
                    element: (
                        <FeatureAccessGuard requiredFeature="economic-calendar">
                            <NewsCalendarPage />
                        </FeatureAccessGuard>
                    ),
                },
            ],
        },
        [route.PRIVATE_ROUTES.boostCheckout]: {
            path: '/checkout/boost',
            element: <BoostCheckoutPage />,
        },
    },
};
